import { CUSTOM_ELEMENTS_SCHEMA } from '@angular/compiler';
import { Component, OnInit, AfterViewInit, ViewChild, Input } from '@angular/core';
import { SettingService } from 'src/app/config/shared/setting/setting.service';
import { ProfileRelationshipManagementDto } from 'src/app/profiles/shared/dto/profile-relationship-management.dto';
import { ProfileRelationSearch } from 'src/app/profiles/shared/profile-relation-search.model';
import { ProfileService } from 'src/app/profiles/shared/profile.service';
import { CreditCardInputComponent } from 'src/app/revenue/credit-card-input/credit-card-input.component';
import { ArcService, ArcFormula, ArcSearch } from 'src/app/revenue/shared/arc';
import { ArbService } from 'src/app/revenue/shared/billing/arb.service';
import { BillingCycle } from 'src/app/revenue/shared/billing/billing-cycle.model';
import { Billing } from 'src/app/revenue/shared/billing/billing.model';
import { BillingService } from 'src/app/revenue/shared/billing/billing.service';
import { ArbCommandResultDto } from 'src/app/revenue/shared/billing/dto/arb-command-result.dto';
import { ArbCreateResultDto, isArbId } from 'src/app/revenue/shared/billing/dto/arb-create-result.dto';
import { ArbCreateDto, initializedArbCreateDto } from 'src/app/revenue/shared/billing/dto/arb-create.dto';
import { ArbReadDto } from 'src/app/revenue/shared/billing/dto/arb-read.dto';
import { CreditCardGateway } from 'src/app/revenue/shared/credit-card-gateway/credit-card-gateway.model';
import { CreditCardGatewayService } from 'src/app/revenue/shared/credit-card-gateway/credit-card-gateway.service';
import { CreditCardPaymentInfo } from 'src/app/revenue/shared/credit-card/credit-card-payment-info.model';
import { PaymentTypeDto } from 'src/app/revenue/shared/payment-types/dto/payment-type.dto';
import { PaymentTypeService } from 'src/app/revenue/shared/payment-types/payment-type.service';
import { PledgeDto } from 'src/app/revenue/shared/pledge/dto/pledge.dto';
import { RevenueItem } from 'src/app/revenue/shared/revenue-items/revenue-item.model';
import { RevenueItemService } from 'src/app/revenue/shared/revenue-items/revenue-item.service';
import { TranslationService, UtilService, NotificationService } from 'src/app/shared';
import {
  CreateObjectValueAccessorProvider,
  CreateObjectValueValidationProvider,
  ObjectValueAccessor,
} from 'src/app/shared/widgets/value-accessors/object-value-accessor';
import { UserRead } from 'src/app/users/shared/user-read.model';
import { UserService } from 'src/app/users/shared/user.service';
import { PrecznService } from 'src/app/integrations/preczn/shared/preczn.service';
import { TokenizeGatewayDto } from 'src/app/revenue/shared/gateway-resolution/dto/tokenize-gateway.dto';
import { AchInputComponent } from 'src/app/revenue/ach-input/ach-input.component';

@Component({
  selector: 'wl-pledge-edit-billing',
  templateUrl: 'pledge-edit-billing.component.html',
  styleUrls: ['pledge-edit-billing.component.less'],
  providers: [
    CreateObjectValueAccessorProvider(PledgeEditBillingComponent),
    CreateObjectValueValidationProvider(PledgeEditBillingComponent),
    ArcService,
    BillingService,
  ],
    standalone: false
})
export class PledgeEditBillingComponent extends ObjectValueAccessor<Billing> implements OnInit, AfterViewInit {
  @ViewChild(CreditCardInputComponent) ccPaymentComponent: CreditCardInputComponent;
  // The ACH counterpart of ccPaymentComponent: processARBChanges() must reach the widget directly
  // to tokenize, because (achInfoChanges) only tells this host about field edits the staff member
  // made - never about the token, which is minted at submit time.
  @ViewChild(AchInputComponent) achPaymentComponent: AchInputComponent;
  public months: Array<string>;
  public billingCycles: Array<BillingCycle>;
  public anchorDays: Array<number>;
  public arcFormulas: Array<ArcFormula>;
  public revenueItems: Array<RevenueItem>;
  public showBaseField: boolean;
  public showFlatFeeField: boolean;
  public itemHasArc = false;
  public arcId = 0;
  public arcCalculation = 0;
  public useArc = false;
  public arbIsEnabled = false;
  public arbSelected = false;
  public paymentTypes: Array<PaymentTypeDto>;
  public selectedPaymentTypeId: number;
  public showCCPayment = false;
  public showACHPayment = false;
  public arbCreate: ArbCreateDto;
  public arbStartDate: Date;
  public gatewayArbId: string;
  public today = new Date();
  public arb: ArbReadDto;
  public relatedProfiles: ProfileRelationshipManagementDto[];
  public allActiveUsers: Array<UserRead>;

  // Pledge billing specific options
  public showAdditionalOptions: boolean = false;
  public showBillingNotes: boolean = false;
  public showOverrideDescription: boolean = false;
  public showCommunicateWithProfile: boolean = false;
  public showAttention: boolean = false;
  public showAllowARB: boolean = false;
  public showSetAsARB: boolean = false;
  public isPreczn = false;
  // Direct-gateway surface: the selected payment type already names the paying CCGatewayID, so
  // this is resolved from that id and never from revenue-item resolution.
  public resolvedGateway: TokenizeGatewayDto | null = null;

  public get isLegacyArb(): boolean {
    return !!this.value?.IsARB && !this.isPreczn;
  }

  public constructor(
    private _arcService: ArcService,
    private _billingService: BillingService,
    private _profileService: ProfileService,
    private _revenueItemService: RevenueItemService,
    private _translationService: TranslationService,
    private _utilService: UtilService,
    private _settingService: SettingService,
    private _paymentTypeService: PaymentTypeService,
    private _arbService: ArbService,
    private _notificationService: NotificationService,
    private _ccGatewayService: CreditCardGatewayService,
    private _userService: UserService,
    private _precznService: PrecznService
  ) {
    super({} as Billing, false);
    this.months = [];
    this.billingCycles = [];
    this.anchorDays = [];
    this.revenueItems = [];
  }

  ngOnInit() {
    this._arcService.searchArcs({ PageNumber: 0, PageSize: 0, Inactive: false } as ArcSearch).then((data) => {
      this.arcFormulas = data.Result.map((x) => ({ ArcID: x.ArcID, Descr: x.Descr, ItemNum: x.ItemNum } as ArcFormula));
      this.itemHasArc = this.arcFormulas.some((x) => x.ItemNum == this.value.ItemNum);
    });

    this.months = this._translationService.getMonths();
    this.anchorDays = Array(31)
      .fill(0, 0, 31)
      .map((x, i) => i + 1);

    this._billingService.getBillingCycles().then((data) => {
      this.billingCycles = data;
    });

    this._revenueItemService.getRevenueItems().then((data) => {
      this.revenueItems = data;
    });

    this._settingService.arbEnabled().then((data: boolean) => {
      this.arbIsEnabled = data;
      this.updateArbStart();
    });

    this._paymentTypeService.getPaymentTypes().then((data: Array<PaymentTypeDto>) => {
      // ValidAch, not AchType: an ACH payment type whose gateway cannot take ACH (Preczn with
      // eCheck off, or a type with no gateway at all) must not be offered here. Offering it and
      // then rendering no input - or rendering one the gateway will reject - is the worse UX, so
      // the dropdown filter and the showACHPayment gate below move together.
      this.paymentTypes = data.filter((x) => x.CreditCardType || x.ValidAch /* || x.CheckType*/);
    });

    this.arbCreate = initializedArbCreateDto();
  }

  ngAfterViewInit() {
    // Set pristine after Angular has initially set the model
    setTimeout(() => {
      this.showBaseField = this._utilService.isNotNullAndGreaterThanZero(this.value.BasePrice);
      this.showFlatFeeField = this._utilService.isNotNullAndGreaterThanZero(this.value.FlatFee);
      this.setPristine();

      if (!this.value.BillingCycle) {
        this.value.BillingCycle = this.billingCycles[0].Name;
      }

      if (this.value.ARBID) {
        this._arbService.getArbByBillingId(this.value.BillingId).then((data: ArbReadDto) => {
          this.arb = data;

          this.arbSelected = true;
          this.gatewayArbId = data.SubscriptionKey;

          if (data.PaymentTypeID) {
            Promise.all([
              this._paymentTypeService.getPaymentTypes(),
              this._ccGatewayService.getCreditCardGateways(),
            ]).then(([paymentTypes, gateways]) => {
              const pt = paymentTypes.find((p) => p.PaymentTypeId === data.PaymentTypeID);
              if (pt && pt.CcGatewayId) {
                const gw = gateways.find((g) => g.CCGatewayID === pt.CcGatewayId);
                const isPrecznGateway = gw ? gw.GatewayTypeID === 7 || gw.GatewayTypeID === 8 : false;
                const isAuthNet = +data.SubscriptionKey >= 1000000;
                this.isPreczn = isPrecznGateway && !isAuthNet;
              }
            });
          }
        });
      }

      this._profileService
        .getProfileRelationsForManagement({ ProfileId: this.value.ProfileId, PageNumber: 0, PageSize: 0 } as ProfileRelationSearch)
        .then((data) => {
          this._profileService.getProfile(this.value.ProfileId).then((profile) => {
            this.relatedProfiles = [
              { ReportName: profile?.ReportName, RelProfileID: profile?.ProfileId } as ProfileRelationshipManagementDto,
              ...data.Result,
            ];
          });
        });

      this._userService.getActiveUsers().then((data) => {
        this.allActiveUsers = data.sort((x, y) => {
          if (x.UserName.toLowerCase() > y.UserName.toLowerCase()) {
            return 1;
          }
          if (x.UserName.toLowerCase() < y.UserName.toLowerCase()) {
            return -1;
          }
          return 0;
        });
      });
    }, 500);
  }

  revenueItemChanged(itemNum: any) {
    this.itemHasArc = this.arcFormulas.some((x) => x.ItemNum == itemNum);
    if (!this.arcFormulas.some((x) => x.ItemNum == itemNum && x.ArcID == this.arcId)) {
      this.arcId = 0;
    }
    const item = this.revenueItems.find((x) => x.ItemNum === parseInt(itemNum, 10));
    this.value.UnitPrice = item.UnitPrice;
    this.value.RevenueItem = item.ItemName;
    this.value.Taxable = item.Taxable;

    this.updateYearlyTotal();
  }

  arcChanged(arcId: number) {
    if (arcId > 0) {
      this._profileService.getCalculateProfileArc(this.value.ProfileId, arcId).then((amount) => {
        this.arcCalculation = amount;
      });
    }
  }

  async save(profileId: number, pledgeId: number): Promise<void> {
    // these fields might be left blank, so update them to 0 if so
    this.value.FlatFee = this.value.FlatFee ? this.value.FlatFee : 0;
    this.value.BasePrice = this.value.BasePrice ? this.value.BasePrice : 0;
    this.value.SoldBy = this.value.SoldBy ? this.value.SoldBy : 0;

    if (this.useArc && this.arcId > 0) {
      this.value.UnitPrice = this.arcCalculation;
      this.value.Quantity = 1;
      this.value.FlatFee = 0;
      this.value.BasePrice = 0;
    }

    if (this.value.BillingId) {
      if (this.isDirty()) {
        this.value.ProfileId = profileId;
        this.value.PledgeID = pledgeId;
        this.value.AmountPerCycle = this.value.UnitPrice * this.value.Quantity + this.value.FlatFee + this.value.BasePrice;
        switch (this.value.BillingCycle.toLowerCase()) {
          case 'every 12 months':
            this.value.YearlyAmount = this.value.AmountPerCycle;
            break;
          case 'every 6 months':
            this.value.YearlyAmount = this.value.AmountPerCycle * 2;
            break;
          case 'every 3 months':
            this.value.YearlyAmount = this.value.AmountPerCycle * 4;
            break;
          case 'every month':
            this.value.YearlyAmount = this.value.AmountPerCycle * 12;
        }
        if (this.value.Taxable) {
          this.value.TaxAmount = await this._revenueItemService.getItemTaxAmount(this.value.ItemNum, this.value.AmountPerCycle);
        }
        return this._billingService.updateBilling(this.value).then(() => {
          return this.processARBChanges();
        });
      } else {
        return this.processARBChanges();
      }
    } else {
      this.value.ProfileId = profileId;
      this.value.PledgeID = pledgeId;
      this.value.AmountPerCycle = this.value.UnitPrice * this.value.Quantity + this.value.FlatFee + this.value.BasePrice;
      switch (this.value.BillingCycle.toLowerCase()) {
        case 'every 12 months':
          this.value.YearlyAmount = this.value.AmountPerCycle;
          break;
        case 'every 6 months':
          this.value.YearlyAmount = this.value.AmountPerCycle * 2;
          break;
        case 'every 3 months':
          this.value.YearlyAmount = this.value.AmountPerCycle * 4;
          break;
        case 'every month':
          this.value.YearlyAmount = this.value.AmountPerCycle * 12;
      }
      if (this.value.Taxable) {
        this.value.TaxAmount = await this._revenueItemService.getItemTaxAmount(this.value.ItemNum, this.value.AmountPerCycle);
      }
      return this._billingService.createBilling(this.value).then((data) => {
        this.value.BillingId = data;
        return this.processARBChanges();
      });
    }
  }

  // A payment is card or ACH - there is no in between, and no surface needs both widgets mounted
  // at once. The two branches are therefore exclusive and card-first, which is the instrument the
  // server already files for a dual-flagged type (ArbManager.CreateLegacyArbSubscription decides
  // it as `if (CreditCardType) ... else if (ACHType)`). Two independently settable checkboxes in
  // the payment-type admin dialog make a dual-flagged type configurable, so this is enforcement,
  // not an assertion: with sequential `if`s such a type mounted both widgets and offered staff an
  // ACH input whose data the server discards.
  selectedPaymentTypeChanged(paymentTypeId: number) {
    this.showCCPayment = false;
    this.showACHPayment = false;
    this.resolvedGateway = null;
    const paymentType = this.paymentTypes.find((x) => x.PaymentTypeId === +paymentTypeId);
    if (paymentType?.CreditCardType) {
      this.setupArbDto();
      this._ccGatewayService.getCreditCardGateways().then((data: Array<CreditCardGateway>) => {
        const selectedGateway = data.find((x) => x.CCGatewayID === paymentType.CcGatewayId);

        if (selectedGateway && selectedGateway.GatewayTypeID === 6) {
          this.ccPaymentComponent.useCardConnect = true;
        } else {
          this.ccPaymentComponent.useCardConnect = false;
        }
      });

      this.updateResolvedGateway(paymentType.CcGatewayId);

      this.showCCPayment = true;
    } else if (paymentType?.ValidAch) {
      // The ACH widget needs its gateway for exactly the same reason the card widget does: without
      // it resolvedGateway stays null, the widget never leaves raw-field mode, and the subscribe
      // posts no Preczn token. The paying CCGatewayID is already in hand on the payment type.
      this.updateResolvedGateway(paymentType.CcGatewayId);

      this.showACHPayment = true;
    }
  }

  private updateResolvedGateway(ccGatewayId: number): void {
    this._precznService.resolveTokenizeGateway(ccGatewayId).then((gateway) => {
      this.resolvedGateway = gateway;
    });
  }

  updateArbStart() {
    this.arbStartDate = new Date(this.value.AnchorMonth + '/' + this.value.AnchorDay + '/' + this.value.StartingYear);
  }

  updateYearlyTotal() {
    let multiplier = 1;

    switch (this.value.BillingCycle) {
      case 'Every 6 Months':
        multiplier = 2;
        break;
      case 'Every 3 Months':
        multiplier = 4;
        break;
      case 'Every Month':
        multiplier = 12;
        break;
    }

    if (this.value.FlatFee) {
      this.value.YearlyAmount = this.value.FlatFee * multiplier;
    } else {
      this.value.YearlyAmount = (+(this.value.UnitPrice ?? 0) * +(this.value.Quantity ?? 1) + +(this.value.BasePrice ?? 0)) * multiplier;
    }
  }

  private setupArbDto() {
    this.arbCreate.BillingId = this.value.BillingId;
    this.arbCreate.ProfileID = this.value.ProfileId;
    this.arbCreate.PaymentTypeID = this.selectedPaymentTypeId;
    this.arbCreate.Inactive = false;
  }

  private mapCCPaymentInfo(ccInfo: CreditCardPaymentInfo) {
    // Card Info
    this.arbCreate.BillingId = this.value.BillingId;
    this.arbCreate.CardNum = ccInfo.CardNum;
    this.arbCreate.Last4Digits = ccInfo.CardNum.substr(ccInfo.CardNum.length - 4, 4);
    this.arbCreate.CCType = ccInfo.CardType;
    this.arbCreate.AmountPerCycle = this.value.AmountPerCycle;
    this.arbCreate.ExpireMonth = ccInfo.ValidThruMonth;
    this.arbCreate.ExpireYear = ccInfo.ValidThruYear;
    this.arbCreate.SecurityCode = ccInfo.SecurityCode;
    this.arbCreate.Descr = this.value.Descr;

    // CC Contact Info
    this.arbCreate.ContactInfo.Name = ccInfo.CreditCardContactInfo.NameOnCard;
    this.arbCreate.ContactInfo.Address1 = ccInfo.CreditCardContactInfo.Address1;
    this.arbCreate.ContactInfo.Address2 = ccInfo.CreditCardContactInfo.Address2;
    this.arbCreate.ContactInfo.City = ccInfo.CreditCardContactInfo.City;
    this.arbCreate.ContactInfo.State = ccInfo.CreditCardContactInfo.State;
    this.arbCreate.ContactInfo.Zip = ccInfo.CreditCardContactInfo.Zip;
    this.arbCreate.ContactInfo.ZipExt = ccInfo.CreditCardContactInfo.ZipExt;
    this.arbCreate.ContactInfo.County = ccInfo.CreditCardContactInfo.County;
    this.arbCreate.ContactInfo.Country = ccInfo.CreditCardContactInfo.Country;
    this.arbCreate.ContactInfo.Phone = ccInfo.CreditCardContactInfo.Phone;
    this.arbCreate.ContactInfo.Ext = ccInfo.CreditCardContactInfo.Ext;
    this.arbCreate.ContactInfo.Email = ccInfo.CreditCardContactInfo.Email;
    this.arbCreate.ContactInfo.IsCCAddress = true;
    this.arbCreate.ContactInfo.IsRefund = false;
    this.arbCreate.ContactInfo.FirstName = ccInfo.CreditCardContactInfo.FirstName;
    this.arbCreate.ContactInfo.LastName = ccInfo.CreditCardContactInfo.LastName;
    this.arbCreate.ContactInfo.OrgName = ccInfo.CreditCardContactInfo.OrgName;

    this.arbCreate.GatewayTokens = ccInfo.GatewayTokens;
  }

  updateACHInfo(achInfo: ArbCreateDto) {
    this.arbCreate = achInfo;
  }

  private arbCreateInFlight: Promise<void> | null = null;

  public get isCreatingArb(): boolean {
    return !!this.arbCreateInFlight;
  }

  private async processARBChanges(): Promise<void> {
    if (this.arbSelected) {
      // Once-only: a billing that already has an ARB (loaded, or just created and not yet refreshed)
      // never reaches prepareForSubmit()/DTO mapping/createArb again. Checked synchronously.
      if (!this.value.ARBID && !this.gatewayArbId) {
        if (this.arbCreateInFlight) {
          return this.arbCreateInFlight;
        }
        const inFlight = this.createArbOnce();
        this.arbCreateInFlight = inFlight;
        const clear = () => {
          if (this.arbCreateInFlight === inFlight) {
            this.arbCreateInFlight = null;
          }
        };
        inFlight.then(clear, clear);
        return inFlight;
      } else {
        if (this.value.Inactive === true && this.arb?.Inactive !== true) {
          // cancel the ARB with the gateway
          this._arbService
            .cancelArbByBillingId(this.value.BillingId)
            .then((data: ArbCommandResultDto) => {
              if (data.Success) {
                const x = this._translationService.getInstant('revenue.pledge.edit.billing.arb-cancel-1');
                const y = this._translationService.getInstant('revenue.pledge.edit.billing.arb-cancel-2');
                const z = this._translationService.getInstant('revenue.pledge.edit.billing.arb-cancel-3');
                this._notificationService.success(x + this.value.BillingId.toString() + y + data.SubscriptionKey + z);
              } else {
                this.revertArbCancelFailure();
              }
            })
            .catch(() => {
              this.revertArbCancelFailure();
            })
            .finally(() => {
              return Promise.resolve().then(() => this.setPristine());
            });
        }
      }
    } else {
      return Promise.resolve().then(() => {
        this.setPristine();
      });
    }
  }

  private async createArbOnce(): Promise<void> {
    try {
      if (this.showCCPayment) {
        if (this.ccPaymentComponent.usePreczn) {
          await this.ccPaymentComponent.prepareForSubmit();
        }
        const ccInfo = this.ccPaymentComponent.getCreditCardPaymentInfo();
        this.mapCCPaymentInfo(ccInfo);
      } else if (this.showACHPayment) {
        // else-if, not a second `if`: card-first mutual exclusion, matching
        // selectedPaymentTypeChanged above. A dual-flagged payment type must file one
        // instrument, and the server picks card.
        //
        // ORDER IS LOAD-BEARING: prepareForSubmit() emits the widget's dto through
        // (achInfoChanges) -> updateACHInfo(), which assigns it to arbCreate WHOLESALE. So the
        // token must land on the widget's dto - and that dto must become arbCreate - before
        // setupArbDto() stamps BillingId/ProfileID/PaymentTypeID on it. Swapping these two lines
        // posts a dto with either no GatewayTokens or a zero BillingId.
        await this.achPaymentComponent.prepareForSubmit();
        //Calling setup here so we grab the BillingId, ProfileId, etc
        this.setupArbDto();
      }

      let result: ArbCreateResultDto;
      try {
        result = await this._arbService.createArb(this.arbCreate);
      } catch (error) {
        this.notifyArbCreateFailure(error?.message ?? '');
        return;
      }

      // if it passes our checks but is rejected by the gateway, the response message is dumped in ExternalArbId.
      if (isArbId(result?.ExternalArbId)) {
        // the gateway created the subscription: never create again, even if it also returned a warning
        this.gatewayArbId = result.ExternalArbId;
        if (result.Error) {
          this._notificationService.error(this.arbBillingLabel() + ': ' + result.Error);
        }
        await this.refreshArbAfterCreate();
      } else {
        this.notifyArbCreateFailure(result?.Error || result?.ExternalArbId || '');
      }
    } finally {
      // a stale token must never be re-sent, whatever the outcome
      // (a copy, so the dto that was handed to createArb keeps what it actually sent)
      if (this.arbCreate) {
        this.arbCreate = { ...this.arbCreate, GatewayTokens: undefined };
      }
    }
  }

  private async refreshArbAfterCreate(): Promise<void> {
    try {
      const data = await this._arbService.getArbByBillingId(this.value.BillingId);
      if (data) {
        this.arb = data;
        this.value.ARBID = data.ARBID;
        this.value.IsARB = true;
        this.gatewayArbId = data.SubscriptionKey || this.gatewayArbId;
      }
    } catch {
      // the reload is best effort: gatewayArbId alone still blocks a second create
    }
    this.setPristine();
  }

  private notifyArbCreateFailure(detail: string): void {
    const retry = this._translationService.getInstant('revenue.pledge.edit.billing.arb-create-retry');
    // the billing identifier accompanies the retry guidance; without it the gateway message stands alone
    this._notificationService.error(retry ? `${this.arbBillingLabel()}: ${detail ? detail + ' ' : ''}${retry}` : detail);
  }

  private arbBillingLabel(): string {
    return `${this.value.BillingId}${this.value.Descr ? ' ' + this.value.Descr : ''}`;
  }

  // A Preczn token ARB set inactive stays inactive whatever the cancel call returns (ATLAS-5775).
  private revertArbCancelFailure(): void {
    if (this.isPreczn) {
      return;
    }
    this.value.Inactive = false;
    this._billingService.updateBilling(this.value);
    this._notificationService.error(this._translationService.getInstant('revenue.pledge.edit.billing.arb-not-canceled'));
  }
}
